/**
 * ModerationGate (web)
 *
 * Two responsibilities, kept in one file because they're the two sides of
 * the same feature and always render together:
 *
 *  1. <BannedScreen /> — full-screen replacement for the entire app when
 *     the signed-in user's `moderationState` is 'banned'. Shows a plain
 *     explanation, a "Contact support" mailto link, and a Sign Out button
 *     so the account isn't stuck in an authenticated-but-blocked loop.
 *     Sits on a red gradient so it reads as intentional rather than as a
 *     browser error.
 *
 *  2. <SuspendedBanner /> — a sticky amber bar rendered above the normal
 *     Routes when the user is 'suspended'. Non-dismissible — the app
 *     itself keeps working for reads; Firestore rules block the writes,
 *     and the banner explains why nothing will save.
 *
 * Both are pure UI. App.tsx decides which to mount based on the result
 * of useModerationState().
 */

import { Ban, Mail, LogOut, Clock } from 'lucide-react';
import { signOutUser } from '../../firebase/auth';

const SUPPORT_EMAIL = 'hello@polishallthethings.com';

export function BannedScreen() {
  const contactSupport = () => {
    const subject = 'Account ban appeal';
    const body =
      'Hi,\n\nMy account has been banned and I would like to appeal or ' +
      'ask why.\n\nMy username: \nAdditional context:\n';
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
      // Reload to fully reset in-memory state; the auth listener would
      // clear the gate on its own, but a hard reload is a cleaner UX
      // (matches the AuthModal onSuccess pattern used elsewhere).
      window.location.href = '/';
    } catch (err) {
      console.warn('[BannedScreen] signOut failed', err);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-6 py-12 bg-gradient-to-b from-red-900 via-red-950 to-indigo-950">
      <div className="max-w-lg w-full flex flex-col items-center text-center">
        {/* Icon circle */}
        <div className="w-20 h-20 rounded-full bg-red-500/25 flex items-center justify-center mb-5">
          <Ban className="w-11 h-11 text-red-200" />
        </div>

        <h1 className="font-heading text-3xl sm:text-4xl text-white mb-3">
          Account banned
        </h1>

        <p className="font-body text-red-100 text-base leading-relaxed mb-8">
          This account has been banned for violating community guidelines.
          If you believe this is in error, contact support and we&apos;ll
          take another look.
        </p>

        <button
          onClick={contactSupport}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-red-900 font-body font-bold text-sm hover:bg-red-50 transition-colors shadow-lg mb-3"
        >
          <Mail className="w-4 h-4" />
          Contact support
        </button>

        <button
          onClick={handleSignOut}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-red-200 hover:text-white font-body font-bold text-sm transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>

        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="mt-6 text-xs font-body text-red-300 hover:text-red-100 select-all"
        >
          {SUPPORT_EMAIL}
        </a>
      </div>
    </div>
  );
}

export function SuspendedBanner() {
  // Non-dismissible by design — no close button. Sticky so it stays
  // visible while the user scrolls; amber to distinguish from the
  // red-shaded grace-period / DMCA warnings and keep the tone as
  // "your account is limited" rather than "the app is broken".
  return (
    <div className="sticky top-0 z-40 w-full bg-amber-600/95 backdrop-blur-sm border-b border-amber-700 shadow-md">
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <Clock className="w-4 h-4 text-white flex-shrink-0" />
        <p className="text-sm font-body font-bold text-white flex-1 leading-snug">
          Your account is suspended. You cannot post, message, or list items.
        </p>
      </div>
    </div>
  );
}
